'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Cookie, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'

const COOKIE_CONSENT_KEY = 'buddify_cookie_consent'

type ConsentStatus = 'pending' | 'accepted' | 'rejected'

export function CookieConsent() {
  const [status, setStatus] = useState<ConsentStatus>('pending')
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Restore browser-only consent after hydration without changing the server markup.
      setStatus(stored as ConsentStatus)
    } else {
      // Show banner after a short delay
      const timer = setTimeout(() => setVisible(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted')
    setStatus('accepted')
    setVisible(false)
    // Enable analytics here
  }

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected')
    setStatus('rejected')
    setVisible(false)
    // Disable analytics here
  }

  if (status !== 'pending') return null

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4"
        >
          <div className="max-w-3xl mx-auto">
            <div className="bg-panel rounded-2xl shadow-xl border border-line p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-brand-green-soft rounded-lg shrink-0">
                  <Cookie className="w-5 h-5 text-brand-green" />
                </div>

                <div className="flex-1">
                  <h3 className="font-semibold text-ink mb-1">
                    We use cookies
                  </h3>
                  <p className="text-sm text-muted mb-4">
                    We use cookies to improve your experience, analyze site
                    traffic, and personalize content. By clicking &quot;Accept&quot;, you
                    consent to our use of cookies.{' '}
                    <Link
                      href="/cookie"
                      className="text-brand-green hover:text-brand-green underline"
                    >
                      Cookie Policy
                    </Link>
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <Button onClick={handleAccept} size="sm">
                      Accept All
                    </Button>
                    <Button onClick={handleReject} variant="secondary" size="sm">
                      Essential Only
                    </Button>
                  </div>
                </div>

                <button
                  onClick={handleReject}
                  className="p-1 text-muted hover:text-muted transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
